import { useTranslation } from "react-i18next";
import { useWorkshopMode } from "../../../../../hooks/useWorkshopMode";
import { CommissionProfile } from "../../../../../core/models/entities/Commission";
import { LogoOptions } from "../../../../../core/models/entities/LogoOptions";
import LoadingElementIsolated from "../../../../components/loading-element/loading-element-isolated";
import { HidePricesToggle } from "../../../customer-create/components/hide-prices-toggle.component";
import {
  CommissionEditorV2,
  RateCard,
} from "../../../../components/commission/commission-editor-v2.component";
import VATField from "../../../../components/vat/VATField";
import { CommissionOptions } from "../commission-editor.component";

interface PricingSectionProps {
  commission: CommissionProfile;
  commissionOptions: CommissionOptions;
  updateCommission: (commission: CommissionProfile) => void;
  logoOptions?: LogoOptions;
  optionsReady: boolean;
  vatRate?: number;
  onVatChange: (value: number) => void;
  hidePrices: boolean;
  onHidePricesChange: (next: boolean) => void;
}

function WorkshopPricingNote() {
  const { t } = useTranslation();
  return (
    <p className="text-sm text-gray-500">
      {t("customerUpdate.workshopPricingNote")}
    </p>
  );
}

/**
 * Pricing: the commission editor is always rendered expanded, structured
 * exactly like my-profile's Commission & VAT section (CommissionEditorV2
 * with the VAT rate as an extra summary card). The data is idle-prefetched
 * by the viewmodel, so the spinner below is a cold-click fallback only.
 */
export function PricingSection({
  commission,
  commissionOptions,
  updateCommission,
  logoOptions,
  optionsReady,
  vatRate,
  onVatChange,
  hidePrices,
  onHidePricesChange,
}: PricingSectionProps) {
  const { t } = useTranslation();
  const workshopMode = useWorkshopMode();
  if (workshopMode) {
    return <WorkshopPricingNote />;
  }

  return (
    <div className="space-y-6">
      <HidePricesToggle
        checked={hidePrices}
        onChange={onHidePricesChange}
        className="flex flex-col space-y-1"
      />

      {optionsReady && logoOptions ? (
        <CommissionEditorV2
          userType="customer"
          commissions={commission}
          onCommissionUpdate={updateCommission}
          logoOptions={logoOptions}
          options={commissionOptions}
          extraSummaryCard={
            <RateCard
              title={t("customerUpdate.vatRate")}
              description={t("customerUpdate.vatRateDescription")}
            >
              <VATField value={vatRate ?? 0} onChange={onVatChange} />
            </RateCard>
          }
        />
      ) : (
        <div className="relative h-24">
          <LoadingElementIsolated transparent />
        </div>
      )}
    </div>
  );
}
